import { useCallback, useEffect, useState } from 'react';
import {
  DanhMucCongDoan,
  DanhMucCongDoanInput,
  DanhMucCongDoanResponse
} from '@/types/schemas/danh-muc-cong-doan.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseDanhMucCongDoanReturn {
  danhMucCongDoans: DanhMucCongDoan[];
  isLoading: boolean;
  addDanhMucCongDoan: (newDanhMucCongDoan: DanhMucCongDoanInput) => Promise<void>;
  updateDanhMucCongDoan: (uuid: string, updatedDanhMucCongDoan: DanhMucCongDoanInput) => Promise<void>;
  deleteDanhMucCongDoan: (uuid: string) => Promise<void>;
  refreshDanhMucCongDoans: () => Promise<void>;
  getDanhMucCongDoanByCode: (code: string) => Promise<DanhMucCongDoan | null>;
  getActiveDanhMucCongDoans: () => Promise<DanhMucCongDoan[]>;
}

/**
 * Hook for managing DanhMucCongDoan (Work Process Catalog) data
 *
 * This hook provides functions to fetch, create, update, and delete work processes.
 */
export const useDanhMucCongDoan = (initialDanhMucCongDoans: DanhMucCongDoan[] = []): UseDanhMucCongDoanReturn => {
  const [danhMucCongDoans, setDanhMucCongDoans] = useState<DanhMucCongDoan[]>(initialDanhMucCongDoans);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchDanhMucCongDoans = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DanhMucCongDoanResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DANH_MUC_CONG_DOAN}/`
      );
      setDanhMucCongDoans(response.data.results);
    } catch (error) {
      console.error('Error fetching work processes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addDanhMucCongDoan = async (newDanhMucCongDoan: DanhMucCongDoanInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.post(`/entities/${entity.slug}/erp/gia-thanh/danh-muc/danh-muc-cong-doan/`, newDanhMucCongDoan);
      await fetchDanhMucCongDoans();
    } catch (error) {
      console.error('Error adding work process:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDanhMucCongDoan = async (uuid: string, updatedDanhMucCongDoan: DanhMucCongDoanInput): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.put(`/entities/${entity.slug}/erp/${QUERY_KEYS.DANH_MUC_CONG_DOAN}/${uuid}/`, updatedDanhMucCongDoan);
      await fetchDanhMucCongDoans();
    } catch (error) {
      console.error('Error updating work process:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDanhMucCongDoan = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.DANH_MUC_CONG_DOAN}/${uuid}/`);
      setDanhMucCongDoans(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting work process:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getDanhMucCongDoanByCode = async (code: string): Promise<DanhMucCongDoan | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get<DanhMucCongDoanResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DANH_MUC_CONG_DOAN}/?ma_bp=${code}`
      );

      if (response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching work process by code:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveDanhMucCongDoans = async (): Promise<DanhMucCongDoan[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DanhMucCongDoanResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DANH_MUC_CONG_DOAN}/?active=true`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching active work processes:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDanhMucCongDoans();
  }, [entity?.slug]);

  return {
    danhMucCongDoans,
    isLoading,
    addDanhMucCongDoan,
    updateDanhMucCongDoan,
    deleteDanhMucCongDoan,
    refreshDanhMucCongDoans: fetchDanhMucCongDoans,
    getDanhMucCongDoanByCode,
    getActiveDanhMucCongDoans
  };
};
